import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Check, X, ArrowLeft, Sparkles, AlertTriangle, Crown, MessageCircle, ShoppingBag, CreditCard, Plus, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import SEO from "../components/SEO";

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [planContract, setPlanContract] = useState(null);
  const [downgradeDialog, setDowngradeDialog] = useState({ open: false, plan: null, comparison: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, plan: null, savedCard: null });
  const [upgradeDialog, setUpgradeDialog] = useState({ open: false, preview: null, loading: false });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchPlans();
    if (user) {
      fetchSubscription();
      fetchPlanContract();
    }
    
    // Handle canceled checkout
    if (searchParams.get("canceled") === "true") {
      toast.info("Checkout was canceled");
    }
  }, [user, searchParams]);

  const fetchPlans = async () => {
    try {
      // Use /api/billing/plans which filters for pricing page display
      const response = await axios.get(`${API}/billing/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`, { withCredentials: true, headers: getAuthHeaders() });
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  const fetchPlanContract = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription/plan-contract`, { withCredentials: true, headers: getAuthHeaders() });
      setPlanContract(response.data);
    } catch (error) {
      console.error("Failed to fetch plan contract:", error);
    }
  };

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSubscribe = async (planKey) => {
    if (!user) {
      navigate("/login");
      return;
    }

    const currentPlanKey = currentSubscription?.current_plan?.key;
    const isActiveSubscriber = currentSubscription?.subscription_status === "active" && currentSubscription?.stripe_subscription_id;
    
    // If user has an active subscription, use the change-plan flow with preview
    if (isActiveSubscriber) {
      const isUpgrade = getPlanOrder(planKey) > getPlanOrder(currentPlanKey);
      const isDowngrade = getPlanOrder(planKey) < getPlanOrder(currentPlanKey);
      
      if (planKey === currentPlanKey) {
        toast.info("You're already on this plan");
        return;
      }
      
      // For downgrades, show the comparison dialog with warning
      if (isDowngrade) {
        try {
          const response = await axios.get(
            `${API}/billing/plan-comparison?target_plan=${planKey}`,
            { withCredentials: true, headers: getAuthHeaders() }
          );
          setDowngradeDialog({
            open: true,
            plan: plans.find(p => p.key === planKey),
            comparison: response.data,
            isActiveSubscriber: true
          });
          return;
        } catch (error) {
          console.error("Failed to fetch plan comparison:", error);
        }
      }
      
      // For upgrades, show preview with proration breakdown
      if (isUpgrade) {
        setUpgradeDialog({ open: true, preview: null, loading: true });
        try {
          const previewResponse = await axios.get(
            `${API}/billing/change-plan/preview?target_plan=${planKey}`,
            { withCredentials: true, headers: getAuthHeaders() }
          );
          setUpgradeDialog({ 
            open: true, 
            preview: previewResponse.data, 
            loading: false 
          });
        } catch (error) {
          console.error("Upgrade preview error:", error);
          
          // If auth error, suggest re-login
          if (error.response?.status === 401) {
            toast.error("Session expired. Please log in again to upgrade your plan.");
            setUpgradeDialog({ open: false, preview: null, loading: false });
            return;
          }
          
          // For other errors, try to proceed with direct change (fallback)
          toast.error(error.response?.data?.detail || "Failed to load upgrade preview. Trying direct upgrade...");
          
          // Fallback: try direct plan change without preview
          try {
            setCheckoutLoading(planKey);
            const response = await axios.post(
              `${API}/billing/change-plan`,
              { plan_key: planKey },
              { withCredentials: true, headers: getAuthHeaders() }
            );
            
            if (response.data.success) {
              toast.success(response.data.message || "Plan upgraded successfully!");
              window.location.href = "/dashboard?subscription=success";
            }
          } catch (changeError) {
            toast.error(changeError.response?.data?.detail || "Failed to change plan");
            setCheckoutLoading(null);
          }
          
          setUpgradeDialog({ open: false, preview: null, loading: false });
        }
        return;
      }
    }

    // For trialing users - check if this is a downgrade
    if (currentPlanKey && getPlanOrder(planKey) < getPlanOrder(currentPlanKey)) {
      try {
        const response = await axios.get(
          `${API}/billing/plan-comparison?target_plan=${planKey}`,
          { withCredentials: true }
        );
        setDowngradeDialog({
          open: true,
          plan: plans.find(p => p.key === planKey),
          comparison: response.data,
          isActiveSubscriber: false
        });
        return;
      } catch (error) {
        console.error("Failed to fetch plan comparison:", error);
      }
    }

    // Check for saved payment method (for new subscriptions)
    try {
      const optionsResponse = await axios.get(
        `${API}/billing/checkout/options?plan_key=${planKey}`,
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      if (optionsResponse.data.has_saved_card) {
        // Show payment method choice dialog
        setPaymentDialog({
          open: true,
          plan: plans.find(p => p.key === planKey),
          savedCard: optionsResponse.data.saved_card,
          planDetails: optionsResponse.data
        });
        return;
      }
    } catch (error) {
      console.error("Failed to fetch checkout options:", error);
    }

    // No saved card, proceed directly to checkout
    proceedWithSubscription(planKey, false);
  };

  // Confirm upgrade with proration charge
  const confirmUpgrade = async () => {
    if (!upgradeDialog.preview) return;
    
    const planKey = upgradeDialog.preview.target_plan.key;
    setCheckoutLoading(planKey);
    
    try {
      const response = await axios.post(
        `${API}/billing/change-plan`,
        { plan_key: planKey },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || "Plan upgraded successfully!");
        window.location.href = "/dashboard?subscription=success";
      }
    } catch (error) {
      console.error("[UPGRADE] Error:", error.response?.data || error);
      const errorDetail = error.response?.data?.detail || "Failed to upgrade plan";
      const requestId = error.response?.headers?.["x-request-id"];
      
      // Show error with request ID for debugging
      if (requestId) {
        toast.error(`${errorDetail} (ref: ${requestId.substring(0, 8)})`);
        console.error(`[UPGRADE] Request ID: ${requestId}`);
      } else {
        toast.error(errorDetail);
      }
      setCheckoutLoading(null);
    }
    
    setUpgradeDialog({ open: false, preview: null, loading: false });
  };

  const proceedWithSubscription = async (planKey, useSavedCard = true) => {
    setCheckoutLoading(planKey);
    setDowngradeDialog({ open: false, plan: null, comparison: null });
    setPaymentDialog({ open: false, plan: null, savedCard: null });
    
    try {
      const response = await axios.post(
        `${API}/billing/checkout/subscription?plan_key=${planKey}&use_saved_card=${useSavedCard}`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      // Check if subscription was created directly (card on file used)
      if (response.data.subscription_created) {
        toast.success("Subscription activated successfully!");
        // Redirect to success page
        window.location.href = response.data.redirect_url;
      } else {
        // Redirect to Stripe Checkout (no saved card or user chose new card)
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setCheckoutLoading(null);
    }
  };

  const getPlanOrder = (key) => {
    const order = { starter: 0, pro: 1, studio: 2 };
    return order[key] ?? 1;
  };

  const sortedPlans = [...plans].sort((a, b) => getPlanOrder(a.key) - getPlanOrder(b.key));

  // Build comprehensive feature list from all plans' feature_details (from DB)
  // This ensures features match exactly what the API returns
  const buildAllFeatures = () => {
    const seenFeatures = new Set();
    const allFeatures = [];
    
    // Process in tier order to maintain proper grouping
    const tierOrder = ['starter', 'pro', 'studio'];
    
    for (const tierKey of tierOrder) {
      const plan = sortedPlans.find(p => p.key === tierKey);
      if (plan?.feature_details) {
        for (const feature of plan.feature_details) {
          if (!seenFeatures.has(feature.key)) {
            seenFeatures.add(feature.key);
            allFeatures.push({
              key: feature.key,
              name: feature.name,
              description: feature.description || "",
              tier: tierKey,
              // Mark studio features as "coming soon" if not yet implemented
              comingSoon: tierKey === 'studio' && !feature.implemented
            });
          }
        }
      }
    }
    
    return allFeatures;
  };

  const allFeatures = buildAllFeatures();

  // Get effective tier for display - use plan contract if available
  const getCurrentPlanKey = () => {
    // Plan contract is the authoritative source
    if (planContract?.tier) {
      return planContract.tier;
    }
    return currentSubscription?.current_plan?.key;
  };

  // Check if user has pending tier change
  const hasPendingChange = () => {
    const tierChangeState = currentSubscription?.tier_change_state;
    return tierChangeState && tierChangeState !== 'no_change' && tierChangeState !== 'NO_CHANGE';
  };

  const getPendingChangeInfo = () => {
    if (!hasPendingChange()) return null;
    
    const state = currentSubscription?.tier_change_state;
    if (state === 'upgrade_pending_payment' || state === 'UPGRADE_PENDING_PAYMENT') {
      return { type: 'upgrade', message: 'Upgrade pending payment confirmation' };
    }
    if (state === 'downgrade_pending' || state === 'DOWNGRADE_PENDING') {
      const pendingTier = currentSubscription?.pending_tier;
      return { 
        type: 'downgrade', 
        message: `Downgrade to ${pendingTier?.charAt(0).toUpperCase() + pendingTier?.slice(1)} scheduled`,
        pendingTier 
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" data-testid="pricing-page">
      <SEO 
        title="Pricing | 360Lumina Photography Business Platform"
        description="Affordable pricing plans for photographers. Start free, scale as you grow. Booking management, galleries, contracts, and payments all included."
        canonical="https://www.360lumina.com/pricing"
      />
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-16">
        <Link 
          to={user ? "/dashboard/billing" : "/"} 
          className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {user ? "Back to Dashboard" : "Back to Home"}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <Camera className="w-8 h-8 text-primary" />
          <span className="text-2xl font-serif">360Lumina</span>
        </div>

        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl tracking-tight mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Choose the plan that fits your photography business.
          </p>
        </div>
      </div>

      {/* Pending Change Notice */}
      {user && hasPendingChange() && (
        <div className="max-w-6xl mx-auto mb-8">
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-400" />
              <div>
                <p className="font-medium text-blue-400">{getPendingChangeInfo()?.message}</p>
                <p className="text-sm text-muted-foreground">
                  {getPendingChangeInfo()?.type === 'downgrade' 
                    ? "Your current plan remains active until the end of your billing period."
                    : "Please complete payment to activate your new plan."}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8">
          {sortedPlans.map((plan, index) => {
            const currentPlanKey = getCurrentPlanKey();
            const isCurrentPlan = currentPlanKey === plan.key;
            const isPro = plan.key === "pro";
            const isStudio = plan.key === "studio";
            const isEarlyAccess = plan.is_early_access;
            const showPrice = plan.show_price !== false;
            
            // Check if this is the pending tier for downgrade
            const pendingInfo = getPendingChangeInfo();
            const isPendingDowngradeTier = pendingInfo?.type === 'downgrade' && pendingInfo?.pendingTier === plan.key;
            
            return (
              <motion.div
                key={plan.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`bg-card border-border relative h-full flex flex-col ${
                  isPro ? "border-primary ring-2 ring-primary/20" : ""
                } ${isStudio ? "border-amber-500/30" : ""}`}>
                  {isPro && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-white px-4 py-1">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  {isEarlyAccess && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-purple-500/80 text-white px-4 py-1 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Early Access
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-0">
                    <div className="flex items-center justify-center gap-2">
                      {isStudio && <Crown className="w-5 h-5 text-amber-400" />}
                      <CardTitle className="text-2xl font-serif">{plan.name}</CardTitle>
                    </div>
                    {plan.description && (
                      <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                    )}
                    <div className="mt-4">
                      {isStudio && !showPrice ? (
                        <div className="text-center">
                          <p className="text-lg text-muted-foreground">Contact us for</p>
                          <p className="text-lg text-amber-400 font-medium">early access pricing</p>
                        </div>
                      ) : (
                        <>
                          <span className="text-5xl font-serif">${plan.monthly_price}</span>
                          <span className="text-muted-foreground">/month</span>
                        </>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-1 flex flex-col pt-8">
                    <ul className="space-y-3 mb-8 flex-1">
                      {allFeatures.map((feature) => {
                        const hasFeature = plan.features?.includes(feature.key);
                        const isComingSoon = feature.comingSoon;
                        return (
                          <li key={feature.key} className="flex items-start gap-3">
                            {hasFeature ? (
                              <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                            ) : (
                              <X className="w-5 h-5 text-muted-foreground/30 shrink-0 mt-0.5" />
                            )}
                            <span className={`flex items-center gap-2 ${hasFeature ? "text-foreground" : "text-muted-foreground/50"}`}>
                              {feature.name}
                              {hasFeature && isComingSoon && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
                                  Soon
                                </Badge>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    {/* CTA Buttons */}
                    {isStudio && isEarlyAccess ? (
                      <div className="space-y-3">
                        <Link to="/studio-contact" className="block">
                          <Button 
                            className="w-full bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30"
                            variant="outline"
                            data-testid="studio-talk-to-us"
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            {plan.cta_text || "Talk to us"}
                          </Button>
                        </Link>
                        {plan.cta_subtext && (
                          <p className="text-xs text-muted-foreground text-center">
                            {plan.cta_subtext}
                          </p>
                        )}
                      </div>
                    ) : plan.admin_only ? (
                      // Admin-only plans (like Legacy) - no self-serve CTA
                      <Button disabled className="w-full" data-testid={`admin-only-${plan.key}`}>
                        Contact Support
                      </Button>
                    ) : isPendingDowngradeTier ? (
                      // Pending downgrade to this tier
                      <Button disabled className="w-full bg-blue-500/20 text-blue-400" data-testid={`pending-${plan.key}`}>
                        <Clock className="w-4 h-4 mr-2" />
                        Scheduled
                      </Button>
                    ) : isCurrentPlan ? (
                      <Button disabled className="w-full" data-testid={`current-plan-${plan.key}`}>
                        Current Plan
                      </Button>
                    ) : currentSubscription?.subscription_status === "active" && 
                        getPlanOrder(plan.key) < getPlanOrder(currentPlanKey) ? (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => handleSubscribe(plan.key)}
                        disabled={checkoutLoading === plan.key}
                        data-testid={`downgrade-${plan.key}`}
                      >
                        {checkoutLoading === plan.key ? (
                          <div className="spinner w-4 h-4" />
                        ) : (
                          "Downgrade"
                        )}
                      </Button>
                    ) : (
                      <Button
                        className={`w-full ${isPro ? "btn-primary" : ""}`}
                        variant={isPro ? "default" : "outline"}
                        onClick={() => handleSubscribe(plan.key)}
                        disabled={checkoutLoading === plan.key}
                        data-testid={`subscribe-${plan.key}`}
                      >
                        {checkoutLoading === plan.key ? (
                          <div className="spinner w-4 h-4" />
                        ) : currentSubscription?.subscription_status === "trialing" ? (
                          "Start Subscription"
                        ) : getPlanOrder(plan.key) > getPlanOrder(currentPlanKey || "starter") ? (
                          "Upgrade"
                        ) : (
                          "Get Started"
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Downgrade Warning Dialog */}
        <Dialog open={downgradeDialog.open} onOpenChange={(open) => !open && setDowngradeDialog({ open: false, plan: null, comparison: null })}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Downgrade to {downgradeDialog.plan?.name}?
              </DialogTitle>
              <DialogDescription>
                You are about to downgrade from {downgradeDialog.comparison?.current_plan?.name} to {downgradeDialog.plan?.name}.
              </DialogDescription>
            </DialogHeader>

            {downgradeDialog.comparison?.features_losing?.length > 0 && (
              <div className="space-y-4">
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <h4 className="font-medium text-red-400 mb-2">Features you will lose:</h4>
                  <ul className="space-y-2">
                    {downgradeDialog.comparison.features_losing.map((feature) => (
                      <li key={feature.key} className="flex items-start gap-2 text-sm">
                        <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">{feature.name}</span>
                          {downgradeDialog.comparison.active_usage_warnings?.[feature.key] && (
                            <p className="text-xs text-red-300 mt-1">
                              {downgradeDialog.comparison.active_usage_warnings[feature.key]}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {Object.keys(downgradeDialog.comparison.active_usage_warnings || {}).length > 0 && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <h4 className="font-medium text-yellow-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Warning: Active Usage Detected
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      You are currently using some features that will not be available on the {downgradeDialog.plan?.name} plan.
                      Your data will not be deleted, but you will not be able to access these features until you upgrade again.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div>
                <p className="text-sm text-muted-foreground">New monthly price:</p>
                <p className="text-2xl font-serif">${downgradeDialog.plan?.monthly_price}/mo</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">You will save:</p>
                <p className="text-lg text-green-400">
                  ${Math.abs(downgradeDialog.comparison?.price_difference || 0).toFixed(2)}/mo
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDowngradeDialog({ open: false, plan: null, comparison: null })}>
                Keep Current Plan
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  // For active subscribers, use change-plan endpoint
                  if (downgradeDialog.isActiveSubscriber) {
                    setCheckoutLoading(downgradeDialog.plan?.key);
                    axios.post(
                      `${API}/billing/change-plan`,
                      { plan_key: downgradeDialog.plan?.key },
                      { withCredentials: true, headers: getAuthHeaders() }
                    ).then(response => {
                      if (response.data.success) {
                        toast.success(response.data.message || "Plan changed successfully!");
                        window.location.href = "/dashboard?subscription=success";
                      }
                    }).catch(error => {
                      toast.error(error.response?.data?.detail || "Failed to change plan");
                      setCheckoutLoading(null);
                    });
                  } else {
                    // For trialing users, proceed with new subscription
                    proceedWithSubscription(downgradeDialog.plan?.key);
                  }
                }}
                disabled={checkoutLoading === downgradeDialog.plan?.key}
              >
                {checkoutLoading === downgradeDialog.plan?.key ? (
                  <div className="spinner w-4 h-4" />
                ) : (
                  "Confirm Downgrade"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Confirmation Dialog with Proration Breakdown */}
        <Dialog open={upgradeDialog.open} onOpenChange={(open) => !open && setUpgradeDialog({ open: false, preview: null, loading: false })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                Confirm Upgrade
              </DialogTitle>
              <DialogDescription>
                {upgradeDialog.preview && (
                  <>Upgrade from {upgradeDialog.preview.current_plan.name} to {upgradeDialog.preview.target_plan.name}</>
                )}
              </DialogDescription>
            </DialogHeader>

            {upgradeDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-8 h-8" />
              </div>
            ) : upgradeDialog.preview && (
              <div className="space-y-4 py-4">
                {/* Proration Breakdown */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Billing Breakdown</h4>
                  
                  {upgradeDialog.preview.proration.credit_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-500">Credit for unused {upgradeDialog.preview.current_plan.name}</span>
                      <span className="text-green-500">-${upgradeDialog.preview.proration.credit_amount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span>New plan ({upgradeDialog.preview.target_plan.name})</span>
                    <span>${upgradeDialog.preview.proration.charge_amount.toFixed(2)}</span>
                  </div>
                  
                  <div className="border-t border-border pt-2 flex justify-between font-medium">
                    <span>Charged Now</span>
                    <span className="text-lg">${upgradeDialog.preview.proration.net_amount.toFixed(2)}</span>
                  </div>
                </div>

                {/* Payment Method */}
                {upgradeDialog.preview.payment_method ? (
                  <div className="border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium">Payment Method</p>
                        <p className="text-sm text-muted-foreground">
                          {upgradeDialog.preview.payment_method.brand} •••• {upgradeDialog.preview.payment_method.last4}
                          <span className="ml-2">
                            Exp {upgradeDialog.preview.payment_method.exp_month}/{upgradeDialog.preview.payment_method.exp_year}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-yellow-500/50 bg-yellow-500/10 rounded-lg p-4">
                    <p className="text-sm text-yellow-600">No payment method on file. You&apos;ll be redirected to add one.</p>
                  </div>
                )}

                {/* Billing Reset Notice */}
                {upgradeDialog.preview.proration.billing_cycle_reset && (
                  <p className="text-xs text-muted-foreground text-center">
                    Your billing date will reset to today
                  </p>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setUpgradeDialog({ open: false, preview: null, loading: false })}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmUpgrade}
                disabled={upgradeDialog.loading || checkoutLoading}
                className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
              >
                {checkoutLoading ? (
                  <div className="spinner w-4 h-4" />
                ) : (
                  <>
                    Confirm Upgrade
                    {upgradeDialog.preview && ` • $${upgradeDialog.preview.proration.net_amount.toFixed(2)}`}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Method Choice Dialog */}
        <Dialog open={paymentDialog.open} onOpenChange={(open) => !open && setPaymentDialog({ open: false, plan: null, savedCard: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <CreditCard className="w-5 h-5 text-primary" />
                Choose Payment Method
              </DialogTitle>
              <DialogDescription>
                Subscribe to {paymentDialog.plan?.name} for ${paymentDialog.planDetails?.monthly_price}/month
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4">
              {/* Use saved card option */}
              <button
                onClick={() => proceedWithSubscription(paymentDialog.plan?.key, true)}
                disabled={checkoutLoading === paymentDialog.plan?.key}
                className="w-full p-4 border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
                data-testid="use-saved-card-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Use card on file</div>
                    <div className="text-sm text-muted-foreground">
                      {paymentDialog.savedCard?.brand} •••• {paymentDialog.savedCard?.last4}
                      <span className="ml-2 text-xs">
                        Exp {paymentDialog.savedCard?.exp_month}/{paymentDialog.savedCard?.exp_year}
                      </span>
                    </div>
                  </div>
                  <Check className="w-5 h-5 text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>

              {/* Use different card option */}
              <button
                onClick={() => proceedWithSubscription(paymentDialog.plan?.key, false)}
                disabled={checkoutLoading === paymentDialog.plan?.key}
                className="w-full p-4 border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
                data-testid="use-different-card-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-muted rounded-lg group-hover:bg-primary/10 transition-colors">
                    <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Use a different card</div>
                    <div className="text-sm text-muted-foreground">
                      Add a new payment method via Stripe Checkout
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setPaymentDialog({ open: false, plan: null, savedCard: null })}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Paid Add-Ons Section */}
        <div className="mt-24" data-testid="addons-section">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif mb-4">Paid Add-Ons</h2>
            <p className="text-muted-foreground">
              Expand your business with powerful additions to your plan
            </p>
          </div>
          
          <Card className="bg-card border-border max-w-2xl mx-auto">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <ShoppingBag className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-serif">Merchandise Store</h3>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                      Coming Soon
                    </Badge>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Sell prints, albums, and custom products directly to your clients. 
                    Powered by print-on-demand integration.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2 mb-4">
                    {[
                      "Print-on-demand integration",
                      "Custom product catalog",
                      "Inventory management",
                      "Client merchandise page"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Available for:</span>
                    <Badge variant="outline" className="text-xs">Pro</Badge>
                    <Badge variant="outline" className="text-xs">Studio</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="mt-24 max-w-3xl mx-auto">
          <h2 className="text-3xl font-serif text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            {[
              {
                q: "How does the free trial work?",
                a: "All new accounts start with a 14-day free trial of Pro features. No credit card required. After the trial, you can choose any plan."
              },
              {
                q: "Can I change my plan later?",
                a: "Yes! You can upgrade at any time and the price difference will be prorated. Downgrades take effect at the end of your billing period."
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit cards through Stripe. Venmo support coming soon."
              },
              {
                q: "What happens to my data if I cancel?",
                a: "Your data remains accessible for 30 days after cancellation. You can export your galleries and client information at any time."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-card border border-border rounded-sm p-6">
                <h3 className="text-lg font-medium mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-24 text-center">
          <p className="text-muted-foreground mb-4">
            Have questions? Need a custom plan for your studio?
          </p>
          <a href="mailto:support@lumina.com" className="text-primary hover:underline">
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
